import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { swallow } from "@/lib/log";
import { StickyNote, FileText, KeyRound, Image as ImageIcon, ScanLine, Search, Film, BookText } from "lucide-react";

const TILES = [
  { key: "notes", label: "Notes", icon: StickyNote, to: "/app/notes" },
  { key: "journal", label: "Journal", icon: BookText, to: "/app/journal" },
  { key: "documents", label: "Documents", icon: FileText, to: "/app/documents" },
  { key: "passwords", label: "Passwords", icon: KeyRound, to: "/app/passwords" },
  { key: "photos", label: "Photos", icon: ImageIcon, to: "/app/photos" },
  { key: "videos", label: "Videos", icon: Film, to: "/app/videos" },
];

export default function Vault() {
  const nav = useNavigate();
  const [counts, setCounts] = useState({ notes: 0, journal: 0, documents: 0, passwords: 0, photos: 0, videos: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [n, j, d, p, ph, v] = await Promise.all([
          api.get("/notes"), api.get("/journal"), api.get("/documents"),
          api.get("/passwords"), api.get("/photos"), api.get("/videos"),
        ]);
        setCounts({ notes: n.data.length, journal: j.data.length, documents: d.data.length, passwords: p.data.length, photos: ph.data.length, videos: v.data.length });
      } catch (e) { swallow("Vault/counts")(e); }
    })();
  }, []);

  return (
    <>
      <TopBar title="Your Vault" subtitle="Everything, safely categorized" showSearch showHome={false} />
      <div className="px-4 py-4 space-y-4">
        <button onClick={() => nav("/app/search")} className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl border border-border bg-card text-left" data-testid="vault-search-button">
          <Search className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Search across notes, journal, docs, photos, videos…</span>
        </button>

        <div className="grid grid-cols-2 gap-3" data-testid="vault-hub">
          {TILES.map(({ key, label, icon: Icon, to }) => (
            <button
              key={key}
              onClick={() => nav(to)}
              className="text-left rounded-2xl border border-border bg-card p-4 hover:shadow-[0_10px_30px_rgba(15,23,42,0.06)] transition"
              data-testid={`vault-hub-${key}-tile`}
            >
              <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-3 text-2xl font-display font-semibold tabular-nums">{counts[key]}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </button>
          ))}
        </div>

        <button
          onClick={() => nav("/app/scanner")}
          className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left"
          data-testid="vault-quick-scan-button"
        >
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <ScanLine className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-semibold text-sm">Quick scan a document</div>
            <div className="text-xs text-muted-foreground">Auto-categorized with OCR</div>
          </div>
        </button>
      </div>
    </>
  );
}
